<script setup>
import headerDefault from "@/components/App/header-default/header-default.vue";
import footerDefault from "@/components/App/footer/footer.vue";
import ticker from "@/components/App/ticker.vue";

import { ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from "vue-router";
import { useCommonComposable } from '@/composables/commonComposable.js';

import { useHead } from "@vueuse/head";
import order from "@/store/modules/order";

const router = useRouter();
const route = useRoute();
const store = useStore();

useHead({
    title: `Заказ №${route.params.number} — Baller | Оригинальные джерси`,
    meta: [
        {name: 'description', content: "Описание страницы"}
    ],
})

const { user, getUser, setImageURL, isMobile } = useCommonComposable();
const orderNumber = ref('');
const actualOrder = ref({})

const getOrder = async () => {
    await store.dispatch('order/fetchOrders', {customerId: user.value[0].ID})
    actualOrder.value = JSON.parse(JSON.stringify(store.state.order.orders)).find(item => item["Номер"] === orderNumber.value)

    if (!actualOrder.value) {
        await router.replace({ name: 'not-found' });
    }

    if (actualOrder.value["Номер"]) {
        await store.dispatch(`order/fetchOrderItems`, {orderId: actualOrder.value["ID"]});
        await store.dispatch(`order/fetchOrderStatuses`, {orderId: actualOrder.value["ID"]});
        actualOrder.value = JSON.parse(JSON.stringify(store.state.order.orders)).find(item => item["Номер"] === orderNumber.value)
    }

    if (actualOrder.value.orderItems) {
        for (let item of actualOrder.value.orderItems) {
            item.image = setImageURL((await getItemImages(item["Код"]))[0])
        }
    }
}

const getItemImages = async (code) => {
    const fetchParams = { productCode: code };
    return store.dispatch('fetchImages', fetchParams)
        .then(() => store.state.images.filter(img => img['Тип'] === 'Заглавная'))
        .catch(err => console.error("Error fetching images:", err));
}

onMounted(async () => {
    orderNumber.value = route.params.number;
    await getUser();
    await getOrder();
})

</script>

<template>
    <div class="silver-background">
        <ticker></ticker>
        <header-default></header-default>
        <div class="container page column-page">
            <div class="row">
                <div class="col-12">
                    <h3 class="page-header">Заказ №{{ $route.params.number }}</h3>
                </div>
            </div>
            <div class="row">
                <div class="col-lg-8 col-md-8 col-12">
                    <div class="order-elems">
                        <h4 class="block-header">Список товаров</h4>
                        <div class="hr"></div>
                        <div class="order-item" v-if="actualOrder.orderItems" v-for="item of actualOrder.orderItems">
                            <router-link :to="`/products/${item['Код']}`" class="item-info">
                                <div class="item-img" :style="'background: url(' + item.image + ')'">
                                </div>
                                <div class="item-text">
                                    <div class="top">
                                        <p class="item-header">{{ item["Название продукта"] }}</p>
                                        <p class="item-size">Размер: {{ item["Размер"] }}</p>
                                    </div>
                                    <p class="item-price">{{ item["Цена по скидке"] }}&#8381;</p>
                                </div>
                            </router-link>
                        </div>
                        <div class="hr"></div>
                        <div class="total-info" v-if="typeof actualOrder.orderItems === 'object'">
                            <span>{{ actualOrder.orderItems.length }} {{ getCaseByNumeral(actualOrder.orderItems.length, 'элемент') }}</span>
                            <span>|</span>
                            <span>Итого: {{ actualOrder.orderItems.reduce((acc, currItem) => { return acc + currItem["Цена по скидке"] }, 0) }}&#8381;</span>
                        </div>
                    </div>
                    <div class="btn" @click="$router.go(-1) || $router.push('/')" v-if="!isMobile">
                        <span>Назад</span>
                    </div>
                </div>
                <div class="col-lg-4 col-md-4 col-12 order-additional-info">
                    <div class="order-elems">
                        <div class="head">
                            <h4 class="block-header">Статус заказа</h4>
                            <h4 class="block-header purple-block-header">{{ actualOrder["Статус"] }}</h4>
                        </div>
                        <div class="hr"></div>
                        <div class="status-elem" v-if="actualOrder.orderStatuses" v-for="item of actualOrder.orderStatuses">
                            <span :class="item['Статус'] === actualOrder['Статус'] ? 'active' : ''">{{ item["Дата"] }}</span>
                            <span :class="item['Статус'] === actualOrder['Статус'] ? 'active' : ''">&#8594;</span>
                            <span :class="item['Статус'] === actualOrder['Статус'] ? 'active' : ''">{{ item["Статус"] }}</span>
                        </div>
                    </div>
                    <div class="order-elems">
                        <h4 class="block-header">Адрес получателя</h4>
                        <div class="address-item-outer">
                            <div class="address-item-inner">
                                <p>{{ actualOrder["Служба доставки"] }}</p>
                                <p v-if="actualOrder['Регион'] === actualOrder['Город']">{{`${actualOrder["Индекс"]}, ${actualOrder["Страна"]}, ${actualOrder["Город"]}, ${actualOrder["Улица"]}, д. ${actualOrder["Дом"]} кв. ${actualOrder["Квартира"]}`}}</p>
                                <p v-else>{{`${actualOrder["Индекс"]}, ${actualOrder["Страна"]}, ${actualOrder["Регион"]}, ${actualOrder["Город"]}, ${actualOrder["Улица"]}, д. ${actualOrder["Дом"]} кв. ${actualOrder["Квартира"]}`}}</p>
                                <p>{{ actualOrder["Имя"] }}</p>
                            </div>
                        </div>
                    </div>
                    <div class="btn default" @click="$router.go(-1) || $router.push('/')" v-if="isMobile">
                        <span>Назад</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <footer-default></footer-default>
</template>

<style scoped lang="scss" src="./style.scss"></style>